import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Skeleton from 'react-loading-skeleton';
import { fetchAllDataByIDAPI } from '../../utils/api';
import ContentWrapper from '../../components/contentwrapper/ContentWrapper';
import { Form, Table, Button } from 'react-bootstrap';
import withAuth from '../../utils/withAuth';

const AgentList = () => {
    const { slug } = useParams();
    const [agents, setAgents] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [center, setCenter] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        navigate(`/agent-list/${slug}`);
        getAgents();
    }, []);

    const getAgents = async () => {
        try {
            setIsLoading(true);
            const res = await fetchAllDataByIDAPI(`hr-get-agent-list-by-center-head/${slug}`);
            if (res?.data?.status === 1) {
                setCenter(res?.data.center_head_name)
                setAgents(Array.isArray(res.data.data) ? res.data.data : []);
            } else {
                toast.warning('Check your connection');
            }
        } catch (e) {
            toast.error('Failed to load agents');
        } finally {
            setIsLoading(false);
        }
    };

    const toggleAgentStatus = async (row) => {
        const id = row?.center_agent_id;
        const current = row?.agent_status === 'Active' ? 'Active' : 'Inactive';
        const next = current === 'Active' ? 'Inactive' : 'Active';

        setAgents((prev) =>
            prev.map((a) => (a.center_agent_id === id ? { ...a, agent_status: next } : a))
        );

        try {
            const res = await fetchAllDataByIDAPI(`hr-inactive-agent/${id}`);
            if (!(res?.data?.status === 1)) {
                throw new Error('API returned non-success');
            }
            toast.success(`Agent marked ${next}.`);
        } catch (e) {
            setAgents((prev) =>
                prev.map((a) => (a.center_agent_id === id ? { ...a, agent_status: current } : a))
            );
            toast.error('Failed to update status');
        }
    };

    const norm = (v) => (typeof v === 'string' ? v.toLowerCase() : String(v ?? '').toLowerCase());
    const q = norm(searchTerm.trim());

    const filteredAgents = useMemo(() => {
        if (!q) return agents;
        return agents.filter((a) => {
            const name = norm(a?.agent_name);
            const emp = norm(a?.agent_emp_id);
            return name.includes(q) || emp.includes(q);
        });
    }, [agents, q]);

    return (
        <div className="companypage">
            <Helmet>
                <title>SATKARTAR:AGENT LIST</title>
            </Helmet>

            <ContentWrapper>
                <div className="card hrpages" style={{marginBottom:"55px"}}>
                    <div className="heading p-2 d-flex flex-wrap gap-2 justify-content-between align-items-center">
                        <h5 className="mb-0 text-uppercase" style={{ fontWeight: 500 }}>
                            AGENT LIST ({filteredAgents.length})
                        </h5>
                        <h5 className="mb-0 text-uppercase" style={{ fontWeight: 600, color:"orange" }}>
                            {center}
                        </h5>

                        <Form.Control
                            style={{ maxWidth: 340 }}
                            type="text"
                            placeholder="Search by Agent Name or Emp ID"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="table pagetable table-responsive mt-3">
                        <Table responsive="sm" size="sm" striped bordered hover>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Name</th>
                                    <th>Emp ID</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    [...Array(6)].map((_, i) => (
                                        <tr key={i}>
                                            <td><Skeleton /></td>
                                            <td><Skeleton /></td>
                                            <td><Skeleton /></td>
                                            <td><Skeleton /></td>
                                            <td><Skeleton /></td>
                                        </tr>
                                    ))
                                ) : filteredAgents.length > 0 ? (
                                    filteredAgents.map((a, idx) => {
                                        const isActive = a?.agent_status === 'Active';
                                        return (
                                            <tr key={`${a.center_agent_id}-${idx}`}>
                                                <td>{idx + 1}</td>
                                                <td>{a?.agent_name || '-'}</td>
                                                <td>{a?.agent_emp_id || '-'}</td>
                                                <td>
                                                    <span className={`badge ${isActive ? 'bg-success' : 'bg-secondary'}`}>
                                                        {isActive ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <Button
                                                        size="sm"
                                                        variant={isActive ? 'outline-danger' : 'outline-secondary'}
                                                        onClick={() => toggleAgentStatus(a)}
                                                        disabled={!isActive}
                                                    >
                                                        {isActive ? 'Mark Inactive' : 'Inactive'}
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={5} className="text-center">No Agent found.</td>
                                    </tr>
                                )}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <th>#</th>
                                    <th>Name</th>
                                    <th>Emp ID</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                </tr>
                            </tfoot>
                        </Table>
                    </div>
                </div>
            </ContentWrapper>
        </div>
    );
};

export default withAuth(AgentList);
